import { useShallow } from 'zustand/react/shallow';

import { getVerkeersdagKey } from '@/domain/conflict/conflictUtils';
import useClientState from '@/state/useClientState';
import { Conflict, ConflictType } from '@/types/Conflicten';

function filterOpConflictTypes(conflictTypes: ConflictType[]) {
  return (conflict: Conflict) => conflictTypes?.includes(conflict.type);
}

function filterOpBetrokkenWerklijnIds(werklijnIds: string[]) {
  return (conflict: Conflict) =>
    conflict.betrokkenWerklijnen?.some((werklijn) => werklijnIds.includes(werklijn.werklijnId)) ?? false;
}

/**
 * Custom hook voor het consumeren van conflicten.
 */
const useConflicten = (
  conflictTypes?: ConflictType[],
  betrokkenWerklijnIds?: string[],
  hideGeaccepteerdeConflicten?: boolean,
) => {
  const conflictenView = useClientState(
    useShallow((state) => {
      let { conflicten } = state;

      if (conflictTypes) {
        conflicten = conflicten.filter(filterOpConflictTypes(conflictTypes));
      }
      if (betrokkenWerklijnIds) {
        conflicten = conflicten.filter(filterOpBetrokkenWerklijnIds(betrokkenWerklijnIds));
      }
      if (hideGeaccepteerdeConflicten) {
        conflicten = conflicten.map((conflict) => {
          const geaccepteerdeElementen = conflict.elementen?.filter((element) => element.isGeaccepteerd) ?? [];
          const nietGeaccepteerdeElementen = conflict.elementen?.filter((element) => !element.isGeaccepteerd) ?? [];

          const hasAcceptedElements = geaccepteerdeElementen.length > 0;
          const allElementsAccepted = hasAcceptedElements && nietGeaccepteerdeElementen.length === 0;

          if (allElementsAccepted) {
            const lastAccepted = geaccepteerdeElementen[geaccepteerdeElementen.length - 1];
            return {
              ...conflict,
              elementen: nietGeaccepteerdeElementen,
              geaccepteerdeElementen,
              oorspronkelijkeDienst: lastAccepted.oorspronkelijkeDienst,
              verkeerdMaterieelLaatsteElement: `${lastAccepted.materieelSoort}-${lastAccepted?.materieelAanduiding}`,
              allElementsAccepted: true,
              partOfElementsAccepted: false,
              werklijnElementIdLaatsteElement: lastAccepted.werklijnElementId,
              lastAcceptedPerDay: Object.values(
                geaccepteerdeElementen.reduce<Record<string, (typeof geaccepteerdeElementen)[0]>>((acc, el) => {
                  const day = getVerkeersdagKey(el.uitvoeringstijd);
                  acc[day] = el;
                  return acc;
                }, {}),
              ),
            };
          }

          return {
            ...conflict,
            elementen: nietGeaccepteerdeElementen,
            geaccepteerdeElementen,
            allElementsAccepted: false,
            partOfElementsAccepted: hasAcceptedElements,
            lastAcceptedPerDay: null,
          };
        });
      }

      return conflicten;
    }),
  );
  return { conflictenView };
};

const useVeiligheidsconflictenIds = () => {
  const werklijnIds = useClientState(
    useShallow((state) => {
      return state.conflicten
        .filter(filterOpConflictTypes(['PERRONLENGTE']))
        .flatMap((c) => c.betrokkenWerklijnen)
        .map((b) => b?.werklijnId);
    }),
  );
  return { werklijnIds };
};

export { useConflicten, useVeiligheidsconflictenIds };
